import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import Title from "../../extra/Title";
import { apiInstanceFetch } from "../../../util/api";

// Howyin tokens
const INK = "#1A1614";
const MUTED = "#8A857F";
const HAIRLINE = "#E8E1D3";
const GOLD = "#D4A129";
const CREAM = "#FFFDF7";
const REDBG = "#FDECEC";
const RED = "#DC2626";
const GREENBG = "#E7F6EC";
const GREEN = "#16A34A";

const loginTypeLabel = (t) =>
  t === 1 ? "Google" : t === 2 ? "Apple" : t === 3 ? "Email" : "—";

const relTime = (d) => {
  if (!d) return "";
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return dayjs(d).format("DD/MM/YY");
};

const Avatar = ({ u, size = 38 }) => (
  <div
    style={{
      width: size, height: size, borderRadius: "50%", background: CREAM,
      border: `1px solid ${HAIRLINE}`, display: "inline-flex", alignItems: "center",
      justifyContent: "center", color: GOLD, fontWeight: 700, fontSize: size * 0.4,
      overflow: "hidden", flexShrink: 0,
    }}
  >
    {u.image ? (
      <img src={u.image} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
    ) : (
      (u.firstName || u.email || "?").charAt(0).toUpperCase()
    )}
  </div>
);

const ActionChip = ({ blocked }) => (
  <span
    style={{
      display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 9px",
      borderRadius: 7, fontSize: 11, fontWeight: 700, lineHeight: 1.6,
      background: blocked ? REDBG : GREENBG, color: blocked ? RED : GREEN,
    }}
  >
    <i className={`fa-solid ${blocked ? "fa-ban" : "fa-circle-check"}`} style={{ fontSize: 10 }} />
    {blocked ? "Blocked" : "Active"}
  </span>
);

const Btn = ({ block, disabled, onClick }) => (
  <button
    disabled={disabled}
    onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: 6, border: "none",
      padding: "7px 14px", borderRadius: 9, fontSize: 12.5, fontWeight: 700,
      cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1,
      background: block ? REDBG : GREENBG, color: block ? RED : GREEN,
    }}
  >
    <i className={`fa-solid ${block ? "fa-lock" : "fa-lock-open"}`} style={{ fontSize: 11 }} />
    {block ? "Block" : "Unblock"}
  </button>
);

const StatCard = ({ label, value, color, icon }) => (
  <div className="col-6 col-md-3 mb-3">
    <div style={{ background: CREAM, border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 16 }}>
      <div className="d-flex align-items-center" style={{ gap: 10 }}>
        <div
          style={{
            width: 38, height: 38, borderRadius: 10, display: "inline-flex",
            alignItems: "center", justifyContent: "center",
            background: "#fff", border: `1px solid ${HAIRLINE}`, color: color || INK,
          }}
        >
          <i className={`fa-solid ${icon}`} />
        </div>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: color || INK, lineHeight: 1 }}>{value ?? 0}</div>
          <div style={{ fontSize: 12, color: MUTED }}>{label}</div>
        </div>
      </div>
    </div>
  </div>
);

const BlockedAccounts = () => {
  const [blocked, setBlocked] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadBlocked = (s = "") =>
    apiInstanceFetch
      .get(`auditLog/blockedUsers?limit=300&search=${encodeURIComponent(s)}`)
      .then((r) => { setBlocked(r.data || []); setTotal(r.total || 0); })
      .catch(() => {});

  const loadActions = () =>
    apiInstanceFetch.get(`auditLog/blockActions?limit=20`).then((r) => setActions(r.data || [])).catch(() => {});

  useEffect(() => { loadBlocked(); loadActions(); }, []);
  useEffect(() => {
    const t = setTimeout(() => loadBlocked(search), 300);
    return () => clearTimeout(t); // eslint-disable-next-line
  }, [search]);

  const doSearch = () => {
    if (q.trim().length < 2) { setResults([]); setSearched(false); return; }
    apiInstanceFetch
      .get(`auditLog/userSearch?q=${encodeURIComponent(q.trim())}`)
      .then((r) => { setResults(r.data || []); setSearched(true); })
      .catch(() => {});
  };

  const setBlock = (user, block) => {
    if (!window.confirm(`${block ? "Block" : "Unblock"} ${user.email || user.firstName || "this account"}?`)) return;
    const reason = window.prompt(`Reason for ${block ? "blocking" : "unblocking"} (optional):`, "") || "";
    setBusy(true);
    apiInstanceFetch
      .post(`auditLog/setBlock`, { userId: user._id, block, reason })
      .then(() => { loadBlocked(search); loadActions(); if (q.trim().length >= 2) doSearch(); })
      .catch(() => {})
      .finally(() => setBusy(false));
  };

  return (
    <div className="mainCategory">
      <Title name="Blocked Accounts" />

      {/* Summary */}
      <div className="row mt-3">
        <StatCard label="Currently blocked" value={total} color={RED} icon="fa-user-lock" />
        <StatCard label="Actions logged" value={actions.length >= 20 ? "20+" : actions.length} color={GOLD} icon="fa-clock-rotate-left" />
      </div>

      {/* Block a user */}
      <div style={{ background: CREAM, border: `1px solid ${HAIRLINE}`, borderRadius: 14, padding: 18, marginBottom: 22 }}>
        <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 12 }}>
          <i className="fa-solid fa-user-shield" style={{ color: GOLD }} />
          <span style={{ fontWeight: 700, color: INK }}>Block a user</span>
        </div>
        <div className="d-flex" style={{ gap: 10 }}>
          <div style={{ position: "relative", flex: 1, maxWidth: 560 }}>
            <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 14, top: 13, color: MUTED, fontSize: 13 }} />
            <input
              type="text"
              className="form-control"
              placeholder="Search name, email, referral code, phone, ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doSearch()}
              style={{ paddingLeft: 38, borderRadius: 10, borderColor: HAIRLINE }}
            />
          </div>
          <button onClick={doSearch} style={{ border: "none", background: GOLD, color: INK, fontWeight: 700, padding: "0 20px", borderRadius: 10 }}>
            Search
          </button>
        </div>

        {searched && results.length === 0 && (
          <div style={{ marginTop: 12, color: MUTED, fontSize: 13 }}>No users match “{q}”.</div>
        )}

        {results.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {results.map((u) => (
              <div key={u._id} className="d-flex align-items-center justify-content-between"
                style={{ padding: "11px 4px", borderBottom: `1px solid ${HAIRLINE}` }}>
                <div className="d-flex align-items-center" style={{ gap: 12 }}>
                  <Avatar u={u} />
                  <div>
                    <div style={{ fontWeight: 600, color: INK, fontSize: 13.5 }}>
                      {((u.firstName || "") + " " + (u.lastName || "")).trim() || "—"}{" "}
                      <span style={{ marginLeft: 4 }}><ActionChip blocked={u.isBlock} /></span>
                    </div>
                    <div style={{ color: MUTED, fontSize: 12 }}>
                      {u.email} · {u.referralCode || "—"} · {loginTypeLabel(u.loginType)}
                    </div>
                  </div>
                </div>
                <Btn block={!u.isBlock} disabled={busy} onClick={() => setBlock(u, !u.isBlock)} />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Currently blocked */}
      <div className="d-flex align-items-center justify-content-between mb-2" style={{ gap: 12, flexWrap: "wrap" }}>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <i className="fa-solid fa-user-lock" style={{ color: RED }} />
          <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>Currently blocked ({total})</span>
        </div>
        <div style={{ position: "relative", width: 300, maxWidth: "100%" }}>
          <i className="fa-solid fa-magnifying-glass" style={{ position: "absolute", left: 12, top: 11, color: MUTED, fontSize: 12 }} />
          <input type="text" className="form-control" placeholder="Filter blocked…" value={search}
            onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 34, borderRadius: 10, borderColor: HAIRLINE }} />
        </div>
      </div>

      <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "hidden" }}>
        {blocked && blocked.length > 0 ? (
          blocked.map((u, i) => (
            <div key={u._id} className="d-flex align-items-center justify-content-between"
              style={{ padding: "13px 16px", borderBottom: i < blocked.length - 1 ? `1px solid ${HAIRLINE}` : "none" }}>
              <div className="d-flex align-items-center" style={{ gap: 12, minWidth: 0 }}>
                <Avatar u={u} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: INK, fontSize: 13.5 }}>
                    {((u.firstName || "") + " " + (u.lastName || "")).trim() || "—"}
                  </div>
                  <div style={{ color: MUTED, fontSize: 12, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {u.email} · {u.referralCode || "—"} · {loginTypeLabel(u.loginType)}
                  </div>
                  <div style={{ color: MUTED, fontSize: 11.5, marginTop: 2 }}>
                    {u.blockedAt ? <>Blocked {relTime(u.blockedAt)}</> : "Blocked"}
                    {u.blockReason ? <span style={{ color: RED }}> · {u.blockReason}</span> : ""}
                  </div>
                </div>
              </div>
              <Btn block={false} disabled={busy} onClick={() => setBlock(u, false)} />
            </div>
          ))
        ) : (
          <div style={{ padding: 36, textAlign: "center", color: MUTED }}>
            <i className="fa-solid fa-shield-halved" style={{ fontSize: 30, color: HAIRLINE }} />
            <div style={{ marginTop: 10 }}>No blocked accounts.</div>
          </div>
        )}
      </div>

      {/* Recent actions */}
      {actions.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 10 }}>
            <i className="fa-solid fa-clock-rotate-left" style={{ color: GOLD }} />
            <span style={{ fontWeight: 700, color: INK, fontSize: 15 }}>Recent actions</span>
          </div>
          <div style={{ background: "#fff", border: `1px solid ${HAIRLINE}`, borderRadius: 14, overflow: "hidden" }}>
            {actions.map((a, i) => {
              const block = a.action === "block";
              return (
                <div key={a._id} className="d-flex align-items-center"
                  style={{ gap: 12, padding: "13px 16px", borderBottom: i < actions.length - 1 ? `1px solid ${HAIRLINE}` : "none" }}>
                  <div style={{
                    width: 34, height: 34, borderRadius: "50%", flexShrink: 0,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: block ? REDBG : GREENBG, color: block ? RED : GREEN,
                  }}>
                    <i className={`fa-solid ${block ? "fa-lock" : "fa-lock-open"}`} style={{ fontSize: 13 }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, color: INK }}>
                      <strong>{a.targetName || a.targetEmail || "Account"}</strong>
                      <span style={{ color: block ? RED : GREEN, fontWeight: 600 }}> {block ? "blocked" : "unblocked"}</span>
                    </div>
                    <div style={{ color: MUTED, fontSize: 12 }}>{a.reason ? a.reason : "No reason given"}</div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: INK }}>{relTime(a.createdAt)}</div>
                    <div style={{ fontSize: 11, color: MUTED }}>{dayjs(a.createdAt).format("DD/MM/YY HH:mm")}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BlockedAccounts;
